/**
 * TenantKartotekaFormBuilderModal — modal projektowania layoutu FORMULARZA kartoteki
 * z akordeonu pozycji menu w kreatorze menu TENANTA (etap b cz.2, 2026-07-03).
 *
 * Odpowiednik hubowego MenuItemFormBuilderModal, uproszczony do realiów tenanta:
 *  - layouty żyją na serwerze (custom_module_layouts / system_module_layouts),
 *    nie w strukturze szablonu — zapis to bezpośredni PUT przez
 *    `TenantKartotekaLayoutActions.saveFormLayout` (translacja kształtu w lib),
 *  - builder (`SystemModuleBuilderPage` embedded) pracuje na REALNYCH fieldId
 *    (żadnej translacji slug↔id — pola przychodzą z getFormBuilderData),
 *  - tryb isAdminEmbedded buildera = zero zapytań sieciowych (externalFields).
 *
 * Bez wewnętrznego dropdownu layoutów (hub go ma) — layout wybiera się w akordeonie,
 * modal edytuje dokładnie jeden. readOnly = „Podgląd" domyślnego layoutu.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { TenantKartotekaFormBuilderData } from '@/lib/preview/tenant-menu-preview';
import { SystemModuleBuilderPage } from '@/pages/system-modules/SystemModuleBuilderPage';

import { BuilderOverlay } from './BuilderOverlay';

interface TenantKartotekaFormBuilderModalProps {
  /** Slug kartoteki (nagłówek + klucz zapisu po stronie rodzica). */
  entitySlug: string;
  /** Dane buildera (pola z realnymi ID + initial config drzewa) — z getFormBuilderData. */
  data: TenantKartotekaFormBuilderData;
  /** Zapis (rodzic robi PUT + invalidację). Wywoływany tylko gdy !readOnly. */
  onSave: (builderTree: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
  /** Podgląd domyślnego layoutu — builder read-only, bez ścieżki zapisu. */
  readOnly?: boolean;
}

export function TenantKartotekaFormBuilderModal({
  entitySlug,
  data,
  onSave,
  onClose,
  readOnly = false,
}: TenantKartotekaFormBuilderModalProps) {
  const { t } = useTranslation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleRequestClose = () => {
    if (hasUnsavedChanges) setShowCloseConfirm(true);
    else onClose();
  };

  const handleBuilderSave = async (layoutConfig: Record<string, unknown>) => {
    if (readOnly || saving) return;
    setSaving(true);
    try {
      await onSave(layoutConfig);
      setHasUnsavedChanges(false);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <BuilderOverlay
        testId="tenant-kartoteka-form-builder-modal"
        onRequestClose={handleRequestClose}
        // Zapis w toku blokuje tło i Esc (X zostaje aktywny, jak w oknach z zapisem).
        dismissible={!saving}
        width="full"
        title={t('admin.templateMenuBuilder.formBuilder.title', {
          defaultValue: 'Projektowanie widoku formularza',
        })}
        subtitle={`${data.entityName}${data.layoutName ? ` · ${data.layoutName}` : ''}`}
        banner={
          readOnly ? (
            <span
              className="rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
              data-testid="tenant-form-builder-read-only-banner"
            >
              {t('admin.menuItemViews.readOnlyBanner', {
                defaultValue: 'Podgląd — tylko do odczytu, nie można edytować',
              })}
            </span>
          ) : undefined
        }
        closeLabel={t('common.close', { defaultValue: 'Zamknij' })}
        closeTestId="tenant-kartoteka-form-builder-close"
      >
        <div
          key={entitySlug}
          className="w-full overflow-hidden rounded-lg border border-border-secondary"
        >
          <SystemModuleBuilderPage
            embeddedMode
            slug={entitySlug}
            initialLayoutConfig={data.initialLayoutConfig}
            onSaveKartotekaLayout={handleBuilderSave}
            onDirtyChange={setHasUnsavedChanges}
            externalFields={data.fields}
            readOnlyMode={readOnly}
            hideEmbeddedLayoutSwitcher
          />
        </div>
      </BuilderOverlay>

      <ConfirmDialog
        open={showCloseConfirm}
        variant="danger"
        title={t('admin.templateMenuBuilder.formBuilder.confirmClose.title', {
          defaultValue: 'Niezapisane zmiany',
        })}
        message={t('admin.templateMenuBuilder.formBuilder.confirmClose.message', {
          defaultValue: 'Masz niezapisane zmiany w układzie. Zamknąć bez zapisu?',
        })}
        confirmLabel={t('admin.templateMenuBuilder.formBuilder.confirmClose.confirm', {
          defaultValue: 'Zamknij bez zapisu',
        })}
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onClose={() => setShowCloseConfirm(false)}
      />
    </>
  );
}
